import React from 'react';
import { Card } from '../ui';
import { useCop } from '../CopApp';
import UpsellyMascot from '../UpsellyMascot';
import serviceImg from '../../../images/service.jpg';

// The core places to work, each with one job — kept friendly and short.
const MODULES = [
	{ ico: '◈', name: 'Command Center', view: 'command-center', text: 'Your daily starting point — what the AI found, what is waiting on you, and what is live right now.' },
	{ ico: '✣', name: 'Clustering Studio', view: 'clustering', text: 'Explore the audience segments we discovered, or create your own custom group to target.' },
	{ ico: '▣', name: 'Campaign Builder', view: 'builder', text: 'Turn a segment into a ready-to-launch campaign — offer, triggers, and a live preview.' },
	{ ico: '⚗', name: 'Experiment Lab', view: 'experiment-lab', text: 'Set up and manage tests to learn which approach truly drives more revenue.' },
	{ ico: '◫', name: 'Intelligence Reporting', view: 'reporting', text: 'The results layer — influenced revenue, proven lift, and your next best opportunities.' },
	{ ico: '◬', name: 'Deep Research', view: 'deep-research', text: 'Ask a thorny strategy question and get a sourced report with evidence, actions, and risks.' },
];

const STEPS = [
	{ n: 1, title: 'Review', text: 'Open the Command Center to see what the AI surfaced and what needs you.' },
	{ n: 2, title: 'Shape', text: 'Refine segments and draft campaigns or experiments for the audiences that matter.' },
	{ n: 3, title: 'Approve', text: 'Every action routes through the Approval Queue — nothing launches without your sign-off.' },
	{ n: 4, title: 'Measure', text: 'Watch holdout-measured lift in Reporting, and feed the learning back in.' },
];

export default function Home() {
	const { navigate, pendingCount } = useCop();

	const NEXT = [
		{ ico: '◈', view: 'command-center', title: 'Start in the Command Center', text: 'See what needs your attention today.' },
		{ ico: '✣', view: 'clustering', title: 'Create a custom group', text: 'Target an audience the recommendations did not surface.' },
		{
			ico: '✓',
			view: 'approvals',
			title: pendingCount > 0 ? `Review ${pendingCount} pending approval${pendingCount === 1 ? '' : 's'}` : 'Check the Approval Queue',
			text: 'Approve, edit, or reject proposed actions.',
		},
	];

	return (
		<main className="cop-page cop-home">
			<section className="cop-home-hero">
				<UpsellyMascot size={140} />
				<div className="cop-home-hero-text">
					<div className="cop-home-eyebrow">Welcome to the Campaign Optimization Platform</div>
					<h1>Find revenue, get it approved, prove it worked.</h1>
					<p>
						Our AI reads your store’s behavior, surfaces the highest-value opportunities, and drafts the campaigns
						and experiments to capture them. Every action is yours to approve before anything goes live.
					</p>
					<div className="cop-home-cta">
						<button className="cop-btn primary" onClick={() => navigate('command-center')}>Go to Command Center →</button>
						<button className="cop-btn" onClick={() => navigate('help')}>Read the full guide</button>
					</div>
				</div>
			</section>

			<section className="cop-home-section">
				<h2 className="cop-home-h2">What you can do here</h2>
				<p className="cop-home-lede">Six places to work, each with one job.</p>
				<div className="cop-help-grid">
					{MODULES.map((m) => (
						<div className="cop-help-tile" key={m.name}>
							<h5>
								<span className="ico">{m.ico}</span>
								{m.name}
							</h5>
							<p>{m.text}</p>
							<button className="cop-link" onClick={() => navigate(m.view)}>Open {m.name} →</button>
						</div>
					))}
				</div>
			</section>

			<section className="cop-home-section">
				<h2 className="cop-home-h2">How it works</h2>
				<p className="cop-home-lede">One simple loop — review, shape, approve, measure.</p>
				<div className="cop-home-steps">
					{STEPS.map((s) => (
						<div className="cop-home-step" key={s.n}>
							<span className="cop-home-step-n">{s.n}</span>
							<div>
								<h5>{s.title}</h5>
								<p>{s.text}</p>
							</div>
						</div>
					))}
				</div>
			</section>

			<section className="cop-home-section">
				<h2 className="cop-home-h2">What to do next</h2>
				<div className="cop-home-next">
					{NEXT.map((c) => (
						<button className="cop-home-next-card" key={c.view} onClick={() => navigate(c.view)}>
							<span className="cop-home-next-ico">{c.ico}</span>
							<b>{c.title}</b>
							<span className="cop-home-next-text">{c.text}</span>
							<span className="cop-home-next-go">→</span>
						</button>
					))}
				</div>
			</section>

			<section className="cop-home-section">
				<h2 className="cop-home-h2">Need a hand?</h2>
				<div className="cop-home-help">
					<Card title="Learn the platform" icon="?">
						<p style={{ margin: '0 0 12px', color: 'var(--text-sec)', fontSize: 12.5, lineHeight: 1.6 }}>
							The guide explains every screen, the approval process, how we measure lift, and the words we use.
						</p>
						<button className="cop-link" onClick={() => navigate('help')}>Open Help &amp; Concepts →</button>
					</Card>

					<div className="cop-banner cop-contact-banner" style={{ margin: 0 }}>
						<img className="cop-contact-avatar" src={serviceImg} alt="Your Upsellit conversion specialist" />
						<div>
							<h3>Talk to a real person.</h3>
							<p>A dedicated specialist who knows your account is ready to help with strategy, setup, or anything in flight.</p>
						</div>
						<a className="cop-btn primary" href="https://us.upsellit.com/contact/" target="_blank" rel="noreferrer">Get in touch →</a>
					</div>
				</div>
			</section>
		</main>
	);
}
